/**
 * SAM tool error catalogue (platform admin): error types and the errors tools raise.
 * Extends platformAdminApi via injectEndpoints (no store changes needed).
 * Backend: api-service app/api/v1/endpoints/tool_errors.py.
 */
import { platformAdminApi } from './platformAdminApi';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
  total?: number;
}

export interface ToolErrorType {
  type: string;
  meaning: string;
  model_action: string;
  retryable: boolean;
  is_active?: boolean;
  error_count: number;
}

export interface ToolErrorTypesData {
  types: ToolErrorType[];
  modules: Record<string, string>;
  /** Every SAM tool that raises at least one catalogued error. */
  tools: string[];
  /** The same tools grouped by the module of the errors they raise. */
  tools_by_module: Record<string, string[]>;
}

export interface ToolError {
  code: string;
  name: string;
  module: string;
  type: string;
  source: 'TOOL' | 'API';
  message: string;
  /** null = use the error type's default */
  retryable: boolean | null;
  description?: string | null;
  is_active: boolean;
  /** The only names the message may use (the values the tool passes). */
  variables: string[];
  /** The SAM tools that raise this error (read from the code). */
  tools: string[];
  /** The raising code takes the type from the caught exception; `type` is only the fallback. */
  type_from_exception?: boolean;
  /** An admin edited the message, so code updates no longer replace it. */
  message_edited?: boolean;
  edited_at?: string | null;
}

export interface ToolErrorFilters {
  module?: string;
  type?: string;
  tool?: string;
  search?: string;
  skip?: number;
  limit?: number;
}

const withTags = platformAdminApi.enhanceEndpoints({ addTagTypes: ['ToolErrorType', 'ToolError'] });

export const toolErrorsApi = withTags.injectEndpoints({
  endpoints: (builder) => ({
    listToolErrorTypes: builder.query<ApiResponse<ToolErrorTypesData>, void>({
      query: () => '/api/v1/platform/tool-errors/types',
      providesTags: ['ToolErrorType'],
    }),
    updateToolErrorType: builder.mutation<
      ApiResponse<ToolErrorType>,
      { type: string; meaning?: string; model_action?: string; retryable?: boolean }
    >({
      query: ({ type, ...body }) => ({ url: `/api/v1/platform/tool-errors/types/${type}`, method: 'PUT', body }),
      invalidatesTags: ['ToolErrorType'],
    }),
    listToolErrors: builder.query<ApiResponse<ToolError[]>, ToolErrorFilters>({
      query: ({ module, type, tool, search, skip, limit }) => ({
        url: '/api/v1/platform/tool-errors',
        params: {
          ...(module ? { module } : {}), ...(type ? { type } : {}), ...(tool ? { tool } : {}), ...(search ? { search } : {}),
          ...(skip ? { skip } : {}), ...(limit ? { limit } : {}),
        },
      }),
      providesTags: ['ToolError'],
    }),
    updateToolError: builder.mutation<
      ApiResponse<ToolError>,
      { code: string; message?: string; retryable?: boolean | null; description?: string | null; is_active?: boolean }
    >({
      query: ({ code, ...body }) => ({ url: `/api/v1/platform/tool-errors/${code}`, method: 'PUT', body }),
      invalidatesTags: ['ToolError', 'ToolErrorType'],
    }),
  }),
});

export const {
  useListToolErrorTypesQuery,
  useUpdateToolErrorTypeMutation,
  useListToolErrorsQuery,
  useUpdateToolErrorMutation,
} = toolErrorsApi;
